import { useEffect, useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Brain, Handshake, Kanban, ListTodo, MessageSquarePlus, Newspaper, Plus, Share2, Users,
} from 'lucide-react'
import {
  Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ALL_NAV_ITEMS, MANTASTIC_ITEM, AUTHORITY_ITEM, CEO_WEEKLY_ITEM } from './nav-config'
import { isOwner } from '@/lib/auth-role'
import { useSearch } from '@/hooks/use-search'
import type { SearchEntityType } from '@/types/api'

// Every jump target the palette offers is derived straight from the sidebar nav
// (core + every group, including Mantastic), so the palette can never drift out
// of sync with the nav the way a hand-maintained list did.
const PAGES: { label: string; href: string; icon: ComponentType<{ className?: string }> }[] =
  ALL_NAV_ITEMS.map((item) => ({ label: item.label, href: item.to, icon: item.icon }))

// One icon per search entity type.
const TYPE_ICON: Record<SearchEntityType, typeof Users> = {
  party: Users,
  opportunity: Kanban,
  task: ListTodo,
  brief: Newspaper,
  content: Share2,
  memory: Brain,
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenChat?: () => void
}

// A tiny value debounce so we don't fire a search request on every keystroke.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function InnerPalette({ onClose, onOpenChat }: { onClose: () => void; onOpenChat?: () => void }) {
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 150)
  const navigate = useNavigate()
  const { data, isFetching, isError, error, refetch } = useSearch(debounced)

  const q = query.trim().toLowerCase()
  const go = (href: string) => { navigate(href); onClose() }

  // Quick actions — a verb the user can fire straight from the palette.
  const ACTIONS: { label: string; icon: typeof Users; run: () => void }[] = [
    { label: 'New task', icon: Plus, run: () => go('/tasks') },
    { label: 'Log a touch', icon: Handshake, run: () => go('/contacts') },
    { label: 'Start a research brief', icon: Newspaper, run: () => go('/briefs') },
    { label: 'Open chat', icon: MessageSquarePlus, run: () => { onClose(); onOpenChat?.() } },
  ]

  // Mantastic is owner-only, so append it to the jump list just for the owner.
  const pages = isOwner()
    ? [...PAGES, { label: AUTHORITY_ITEM.label, href: AUTHORITY_ITEM.to, icon: AUTHORITY_ITEM.icon }, { label: CEO_WEEKLY_ITEM.label, href: CEO_WEEKLY_ITEM.to, icon: CEO_WEEKLY_ITEM.icon }, { label: MANTASTIC_ITEM.label, href: MANTASTIC_ITEM.to, icon: MANTASTIC_ITEM.icon }]
    : PAGES

  const filteredActions = q ? ACTIONS.filter((a) => a.label.toLowerCase().includes(q)) : ACTIONS
  const filteredPages = q ? pages.filter((p) => p.label.toLowerCase().includes(q)) : pages
  const groups = data?.groups ?? []

  const hasAny = filteredActions.length + filteredPages.length + groups.length > 0
  const searching = debounced.length >= 2
  const showEmpty = !isFetching && !isError && searching && !hasAny

  return (
    // shouldFilter is off: pages/actions are filtered here, entities by the server.
    <Command shouldFilter={false} className="bg-transparent">
      <DialogTitle className="sr-only">Search FRIDAY</DialogTitle>
      <CommandInput
        placeholder="Search contacts, opportunities, tasks, notes… or jump to a page"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[28rem]">
        {isError && (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            <p>{(error as Error)?.message ?? 'Search is having a moment. Give it another go in a sec.'}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 text-accent underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        )}

        {showEmpty && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Nothing matched "{query.trim()}". Ask Friday to dig through the Brain in chat.
          </div>
        )}

        {filteredActions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {filteredActions.map((a) => (
              <CommandItem key={a.label} value={`action ${a.label}`} onSelect={a.run}>
                <a.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                {a.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {groups.map((group) => {
          const Icon = TYPE_ICON[group.type]
          return (
            <div key={group.type}>
              <CommandSeparator />
              <CommandGroup heading={group.label}>
                {group.results.map((hit) => (
                  <CommandItem
                    key={`${hit.type}-${hit.id}`}
                    value={`${hit.type} ${hit.id} ${hit.label}`}
                    onSelect={() => go(hit.path)}
                  >
                    <Icon className="mr-2 h-4 w-4 shrink-0 text-accent" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm text-head">{hit.label}</span>
                      {hit.snippet && (
                        <span className="truncate text-xs text-muted-foreground">{hit.snippet}</span>
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          )
        })}

        {filteredPages.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Go to">
              {filteredPages.map((p) => (
                <CommandItem key={p.href} value={`page ${p.label}`} onSelect={() => go(p.href)}>
                  <p.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  {p.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  )
}

export function CommandPalette({ open, onOpenChange, onOpenChat }: CommandPaletteProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-xl overflow-hidden">
        <InnerPalette onClose={() => onOpenChange(false)} onOpenChat={onOpenChat} />
      </DialogContent>
    </Dialog>
  )
}
